import React from "react";
import {
  EvaluationCriteria,
  FrameworkCatalogEntry,
} from "../../../model_gen/evaluation";
import { Checkbox } from "../../ui/checkbox";
import { InfoIcon } from "lucide-react";
import { Tooltip } from "antd";
import { renderCriteriaTooltip } from "./CriteriaTooltip";

interface FrameworkTabsProps {
  frameworkOptions: { id: string; name: string; description: string }[];
  activeFramework: string;
  setActiveFramework: (fw: string) => void;
  selectedCountForFramework: (fwId: string) => number;
}

const CountBadge = ({ count }: { count: number }) =>
  count > 0 ? (
    <span className="ml-2 inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-xs font-gc-med rounded-full bg-maitai-green text-white">
      {count}
    </span>
  ) : null;

export const FrameworkTabs: React.FC<FrameworkTabsProps> = ({
  frameworkOptions,
  activeFramework,
  setActiveFramework,
  selectedCountForFramework,
}) => (
  <div
    className="grid border-b border-border"
    style={{
      gridTemplateColumns: `repeat(${frameworkOptions.length + 1}, 1fr)`,
    }}
  >
    {frameworkOptions.map((fw) => (
      <button
        key={fw.id}
        onClick={() => setActiveFramework(fw.id)}
        className={`py-2.5 text-sm text-center transition-colors font-gc-reg border-b-2 -mb-px ${
          activeFramework === fw.id
            ? "border-maitai-green text-maitai-green font-gc-med"
            : "border-transparent text-muted-foreground hover:text-foreground"
        }`}
      >
        {fw.name}
        <CountBadge count={selectedCountForFramework(fw.id)} />
      </button>
    ))}
    <button
      onClick={() => setActiveFramework("CUSTOM")}
      className={`py-2.5 text-sm text-center transition-colors font-gc-reg border-b-2 -mb-px ${
        activeFramework === "CUSTOM"
          ? "border-maitai-green text-maitai-green font-gc-med"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      Custom
      <CountBadge count={selectedCountForFramework("CUSTOM")} />
    </button>
  </div>
);

interface FrameworkHeaderProps {
  description: string;
  hasIds: boolean;
  allSelected: boolean;
  toggleAll: () => void;
}

export const FrameworkHeader: React.FC<FrameworkHeaderProps> = ({
  description,
  hasIds,
  allSelected,
  toggleAll,
}) => (
  <div className="space-y-3">
    <p className="text-xs text-muted-foreground font-gc-reg">{description}</p>
    {hasIds && (
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/50 transition-colors">
        <Checkbox
          id="select-all-framework"
          checked={allSelected}
          onCheckedChange={toggleAll}
        />
        <label
          htmlFor="select-all-framework"
          className="text-sm font-gc-med text-foreground cursor-pointer"
        >
          Select All
        </label>
      </div>
    )}
  </div>
);

interface FrameworkCriteriaListProps {
  criteria: EvaluationCriteria[];
  catalogEntry: FrameworkCatalogEntry | undefined;
  selectedCriteriaIds: number[];
  toggleCriteria: (id: number) => void;
}

export const FrameworkCriteriaList: React.FC<FrameworkCriteriaListProps> = ({
  criteria,
  catalogEntry,
  selectedCriteriaIds,
  toggleCriteria,
}) => (
  <div className="space-y-1">
    {criteria.length > 0
      ? criteria.map((crit) => {
          const isSelected = selectedCriteriaIds.includes(crit.id!);
          const catEntry = catalogEntry?.criteria?.find(
            (c) => c.name === crit.name
          );
          return (
            <div
              key={crit.id}
              className="flex items-center gap-3 pl-9 pr-3 py-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                id={`llm-${crit.id}`}
                checked={isSelected}
                onCheckedChange={() => toggleCriteria(crit.id!)}
              />
              <label
                htmlFor={`llm-${crit.id}`}
                className="text-sm font-gc-med text-foreground cursor-pointer"
              >
                {crit.name}
              </label>
              <Tooltip
                placement="right"
                title={renderCriteriaTooltip(
                  crit.instructions
                    ? crit
                    : { ...crit, instructions: catEntry?.description }
                )}
                overlayStyle={{ maxWidth: 680 }}
              >
                <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
              </Tooltip>
            </div>
          );
        })
      : catalogEntry?.criteria?.map((entry) => (
          <div
            key={entry.name}
            className="flex items-center gap-3 pl-9 pr-3 py-2 rounded-lg hover:bg-muted/50 transition-colors opacity-60"
          >
            <Checkbox disabled />
            <span className="text-sm font-gc-med text-foreground">
              {entry.name}
            </span>
            <Tooltip
              placement="right"
              title={renderCriteriaTooltip({
                name: entry.name,
                instructions: entry.description,
              })}
              overlayStyle={{ maxWidth: 680 }}
            >
              <InfoIcon className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
            </Tooltip>
          </div>
        ))}
  </div>
);
